// hooks/useCategoryParams.ts

import { useParams, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useState } from "react";
// Funkcja pomocnicza do konwersji tekstu na slug - zachowuje polskie znaki
const createSlug = text => {
  const slug = text.toLowerCase().replace(/\s+/g, "-");
  // Użyj encodeURIComponent tylko jeśli tekst zawiera polskie znaki
  return /[ąćęłńóśźż]/.test(slug) ? encodeURIComponent(slug) : slug;
};

// Funkcja pomocnicza do parsowania kolorów i rozmiarów z URL
const parseColorsAndSizesFromURL = (searchParams, filterData) => {
  let colorParam = searchParams.get("kolor");
  let sizeParam = searchParams.get("rozmiar");

  // Obsługa podwójnego kodowania URL
  if (colorParam && colorParam.includes("%25")) {
    colorParam = decodeURIComponent(colorParam);
  }
  if (sizeParam && sizeParam.includes("%25")) {
    sizeParam = decodeURIComponent(sizeParam);
  }
  let featureParam = searchParams.get("cecha");
  // Obsługa podwójnego kodowania URL
  if (featureParam && featureParam.includes("%25")) {
    featureParam = decodeURIComponent(featureParam);
  }
  const colors = [];
  const sizes = [];
  const features = [];

  // Parsowanie kolorów
  if (colorParam && filterData?.colors) {
    console.log("=== COLOR PARSING DEBUG ===");
    console.log("colorParam from URL:", colorParam);
    console.log("Available colors in filterData:", filterData.colors.map(c => ({
      id: c.id,
      label: c.label,
      slug: createSlug(c.label)
    })));
    const colorSlugs = colorParam.split(",");
    console.log("colorSlugs split:", colorSlugs);
    colorSlugs.forEach(colorSlug => {
      console.log("Looking for color with slug:", colorSlug);
      const matchingColor = filterData.colors.find(color => {
        const colorSlugFromLabel = createSlug(color.label);
        console.log(`Comparing "${colorSlug}" with "${colorSlugFromLabel}" (from "${color.label}")`);
        // Porównaj bezpośrednio lub po dekodowaniu URL
        const matches = colorSlugFromLabel === colorSlug || (colorSlugFromLabel.includes("%") ? decodeURIComponent(colorSlugFromLabel) === colorSlug : false) || (colorSlug.includes("%") ? colorSlugFromLabel === decodeURIComponent(colorSlug) : false);
        if (matches) console.log("✓ MATCH FOUND:", color.label);
        return matches;
      });
      if (matchingColor) {
        console.log("Adding color to results:", matchingColor.label);
        colors.push(matchingColor);
      } else {
        console.log("❌ No matching color found for:", colorSlug);
      }
    });
    console.log("Final colors array:", colors.map(c => c.label));
    console.log("=== END COLOR PARSING DEBUG ===");
  }

  // Parsowanie rozmiarów
  if (sizeParam && filterData?.sizes) {
    console.log("=== SIZE PARSING DEBUG ===");
    console.log("sizeParam from URL:", sizeParam);
    console.log("Available sizes in filterData:", filterData.sizes.map(s => ({
      id: s.id,
      label: s.label,
      slug: createSlug(s.label)
    })));
    const sizeSlugs = sizeParam.split(",");
    console.log("sizeSlugs split:", sizeSlugs);
    sizeSlugs.forEach(sizeSlug => {
      console.log("Looking for size with slug:", sizeSlug);
      const matchingSize = filterData.sizes.find(size => {
        const sizeSlugFromLabel = createSlug(size.label);
        console.log(`Comparing "${sizeSlug}" with "${sizeSlugFromLabel}" (from "${size.label}")`);
        const matches = sizeSlugFromLabel === sizeSlug || (sizeSlugFromLabel.includes("%") ? decodeURIComponent(sizeSlugFromLabel) === sizeSlug : false) || (sizeSlug.includes("%") ? sizeSlugFromLabel === decodeURIComponent(sizeSlug) : false);
        if (matches) console.log("✓ MATCH FOUND:", size.label);
        return matches;
      });
      if (matchingSize) {
        console.log("Adding size to results:", matchingSize.label);
        sizes.push(matchingSize);
      } else {
        console.log("❌ No matching size found for:", sizeSlug);
      }
    });
    console.log("Final sizes array:", sizes.map(s => s.label));
    console.log("=== END SIZE PARSING DEBUG ===");
  }

  // Parsowanie cech
  console.log("=== PRE-FEATURES CHECK ===");
  console.log("featureParam:", featureParam);
  console.log("filterData.features exists:", !!filterData?.features);
  console.log("filterData.features length:", filterData?.features?.length || 0);
  console.log("filterData.features:", filterData?.features);
  console.log("=== END PRE-FEATURES CHECK ===");
  if (featureParam && filterData?.features) {
    console.log("=== FEATURES PARSING DEBUG ===");
    console.log("featureParam from URL:", featureParam);

    // Zbierz wszystkie wartości cech z różnych kategorii
    const allFeatureValues = [];
    filterData.features.forEach(featureCategory => {
      if (featureCategory.values && Array.isArray(featureCategory.values)) {
        allFeatureValues.push(...featureCategory.values);
      }
    });
    console.log("All available feature values:", allFeatureValues.map(f => ({
      id: f.id,
      label: f.label,
      slug: createSlug(f.label)
    })));
    const featureSlugs = featureParam.split(",");
    console.log("featureSlugs split:", featureSlugs);
    featureSlugs.forEach(featureSlug => {
      console.log("Looking for feature with slug:", featureSlug);
      const matchingFeature = allFeatureValues.find(feature => {
        const featureSlugFromLabel = createSlug(feature.label);
        console.log(`Comparing "${featureSlug}" with "${featureSlugFromLabel}" (from "${feature.label}")`);
        const matches = featureSlugFromLabel === featureSlug || (featureSlugFromLabel.includes("%") ? decodeURIComponent(featureSlugFromLabel) === featureSlug : false) || (featureSlug.includes("%") ? featureSlugFromLabel === decodeURIComponent(featureSlug) : false);
        if (matches) console.log("✓ MATCH FOUND:", feature.label);
        return matches;
      });
      if (matchingFeature) {
        console.log("Adding feature to results:", matchingFeature.label);
        features.push(matchingFeature);
      } else {
        console.log("❌ No matching feature found for:", featureSlug);
      }
    });
    console.log("Final features array:", features.map(f => f.label));
    console.log("=== END FEATURES PARSING DEBUG ===");
  } else {
    console.log("No featureParam or features in filterData:", {
      featureParam,
      hasFeatures: !!filterData?.features,
      featuresCount: filterData?.features?.length || 0
    });
  }
  return {
    colors,
    sizes,
    features
  };
};
export const useCategoryParams = () => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const params = useParams();
  const slug = Array.isArray(params.slug) ? params.slug[0] : params.slug || "";

  // Selected filters (dla UI)
  const [selectedMinPrice, setSelectedMinPrice] = useState(null);
  const [selectedMaxPrice, setSelectedMaxPrice] = useState(null);
  const [showOnSaleOnly, setShowOnSaleOnly] = useState(false);
  const [selectedOption, setSelectedOption] = useState("default");
  const [selectedColorOptions, setSelectedColorOptions] = useState([]);
  const [selectedSizeOptions, setSelectedSizeOptions] = useState([]);
  const [selectedFeatureOptions, setSelectedFeatureOptions] = useState([]);

  // Applied filters (aktualnie zastosowane)
  const [appliedColorOptions, setAppliedColorOptions] = useState([]);
  const [appliedSizeOptions, setAppliedSizeOptions] = useState([]);
  const [appliedFeatureOptions, setAppliedFeatureOptions] = useState([]);
  const [appliedMinPrice, setAppliedMinPrice] = useState(0);
  const [appliedMaxPrice, setAppliedMaxPrice] = useState(0);
  const [appliedOnSaleOnly, setAppliedOnSaleOnly] = useState(false);
  const updateURL = useCallback((colors, sizes, features, minPrice, maxPrice, onSaleOnly, sort, filterData) => {
    const url = new URL(window.location.href);

    // Usuń stare parametry filtrów
    ["kolor", "rozmiar", "cena_od", "cena_do", "promocje", "sortowanie", "cecha"].forEach(param => {
      url.searchParams.delete(param);
    });

    // Dodaj kolory
    if (colors.length > 0) {
      const colorSlugs = colors.map(option => createSlug(option.label));
      url.searchParams.set("kolor", colorSlugs.join(","));
    }

    // Dodaj rozmiary
    if (sizes.length > 0) {
      const sizeSlugs = sizes.map(option_0 => createSlug(option_0.label));
      url.searchParams.set("rozmiar", sizeSlugs.join(","));
    }

    // Dodaj cechy
    if (features.length > 0) {
      const featureSlugs = features.map(option_1 => createSlug(option_1.label));
      url.searchParams.set("cecha", featureSlugs.join(","));
    }

    // Dodaj ceny tylko jeśli różnią się od domyślnych
    if (filterData) {
      const defaultMinPrice = Math.floor(Number(filterData.minPrice || 0));
      const defaultMaxPrice = Math.floor(Number(filterData.maxPrice || 0));
      if (minPrice !== defaultMinPrice) {
        url.searchParams.set("cena_od", minPrice.toString());
      }
      if (maxPrice !== defaultMaxPrice) {
        url.searchParams.set("cena_do", maxPrice.toString());
      }
    }

    // Dodaj promocje
    if (onSaleOnly) {
      url.searchParams.set("promocje", "true");
    }

    // Dodaj sortowanie jeśli różne od domyślnego
    if (sort && sort !== "default") {
      url.searchParams.set("sortowanie", sort);
    }

    // Użyj router.replace dla lepszej integracji z Next.js
    router.replace(url.pathname + url.search, {
      scroll: false
    });

    // Zaktualizuj applied filters
    setAppliedColorOptions(colors);
    setAppliedSizeOptions(sizes);
    setAppliedFeatureOptions(features);
    setAppliedMinPrice(minPrice);
    setAppliedMaxPrice(maxPrice);
    setAppliedOnSaleOnly(onSaleOnly);
  }, [router]);
  const parseFiltersFromURL = useCallback(filterData_0 => {
    console.log("parseFiltersFromURL called with filterData:", filterData_0);
    const priceFromParam = searchParams.get("cena_od");
    const priceToParam = searchParams.get("cena_do");
    const onSaleParam = searchParams.get("promocje") === "true";
    const sortParam = searchParams.get("sortowanie") || "default";
    const {
      colors: colors_0,
      sizes: sizes_0,
      features: features_0
    } = parseColorsAndSizesFromURL(searchParams, filterData_0);
    const defaultMinPrice_0 = Math.floor(Number(filterData_0?.minPrice || 0));
    const defaultMaxPrice_0 = Math.floor(Number(filterData_0?.maxPrice || 0));
    const minPriceFromUrl = priceFromParam ? parseInt(priceFromParam) : null;
    const maxPriceFromUrl = priceToParam ? parseInt(priceToParam) : null;
    console.log("=== PRICE & SORT PARSING DEBUG ===");
    console.log("Price params from URL:", {
      cena_od: priceFromParam,
      cena_do: priceToParam,
      promocje: onSaleParam,
      sortowanie: sortParam
    });
    console.log("Filter data price range:", {
      defaultMinPrice: defaultMinPrice_0,
      defaultMaxPrice: defaultMaxPrice_0,
      minPriceFromUrl,
      maxPriceFromUrl
    });
    console.log("=== END PRICE & SORT PARSING DEBUG ===");
    console.log("parseFiltersFromURL results:", {
      colors: colors_0.length,
      sizes: sizes_0.length,
      features: features_0.length,
      minPrice: minPriceFromUrl,
      maxPrice: maxPriceFromUrl,
      onSaleOnly: onSaleParam,
      sort: sortParam,
      priceFromParam,
      priceToParam,
      defaultMinPrice: defaultMinPrice_0,
      defaultMaxPrice: defaultMaxPrice_0
    });
    return {
      colors: colors_0,
      sizes: sizes_0,
      features: features_0,
      minPrice: minPriceFromUrl ?? defaultMinPrice_0,
      maxPrice: maxPriceFromUrl ?? defaultMaxPrice_0,
      onSaleOnly: onSaleParam,
      sort: sortParam
    };
  }, [searchParams]);

  // GŁÓWNA ZMIANA: Synchronizuj filtry z URL
  const syncFiltersFromURL = useCallback(filterData_1 => {
    console.log("syncFiltersFromURL called with filterData:", filterData_1);
    const urlFilters = parseFiltersFromURL(filterData_1);
    console.log("syncFiltersFromURL - parsed urlFilters:", urlFilters);

    // BEZPOŚREDNIO ustaw wszystkie wartości na podstawie parsowanych danych
    // Dla cen - ustaw tylko jeśli są w URL, inaczej zostaw null (nie ustawiaj wartości z filterData)
    const priceFromParam_0 = searchParams.get("cena_od");
    const priceToParam_0 = searchParams.get("cena_do");
    setSelectedColorOptions(urlFilters.colors);
    setSelectedSizeOptions(urlFilters.sizes);
    setSelectedFeatureOptions(urlFilters.features);
    setSelectedMinPrice(priceFromParam_0 ? parseInt(priceFromParam_0) : null);
    setSelectedMaxPrice(priceToParam_0 ? parseInt(priceToParam_0) : null);
    setShowOnSaleOnly(urlFilters.onSaleOnly);
    setSelectedOption(urlFilters.sort);
    setAppliedColorOptions(urlFilters.colors);
    setAppliedSizeOptions(urlFilters.sizes);
    setAppliedFeatureOptions(urlFilters.features);
    // Dla applied cen też nie ustawiaj domyślnych wartości automatycznie
    setAppliedMinPrice(priceFromParam_0 ? parseInt(priceFromParam_0) : Math.floor(Number(filterData_1?.minPrice || 0)));
    setAppliedMaxPrice(priceToParam_0 ? parseInt(priceToParam_0) : Math.floor(Number(filterData_1?.maxPrice || 0)));
    setAppliedOnSaleOnly(urlFilters.onSaleOnly);
    console.log("syncFiltersFromURL - state updated with:", {
      minPrice: urlFilters.minPrice,
      maxPrice: urlFilters.maxPrice,
      colors: urlFilters.colors.length,
      sizes: urlFilters.sizes.length,
      features: urlFilters.features.length
    });
  }, [parseFiltersFromURL]);

  // USUNIĘTE - nie rób nic przy zmianie searchParams
  // useEffect(() => {
  //   const sortParam = searchParams.get("sortowanie") || "default";
  //   const onSaleParam = searchParams.get("promocje") === "true";
  //   setSelectedOption(sortParam);
  //   setShowOnSaleOnly(onSaleParam);
  // }, [searchParams]);

  return {
    slug,
    selectedMinPrice,
    selectedMaxPrice,
    showOnSaleOnly,
    selectedOption,
    selectedColorOptions,
    selectedSizeOptions,
    selectedFeatureOptions,
    appliedColorOptions,
    appliedSizeOptions,
    appliedMinPrice,
    appliedMaxPrice,
    appliedOnSaleOnly,
    appliedFeatureOptions,
    updateURL,
    parseFiltersFromURL,
    syncFiltersFromURL,
    setSelectedMinPrice,
    setSelectedMaxPrice,
    setShowOnSaleOnly,
    setSelectedOption,
    setSelectedColorOptions,
    setSelectedSizeOptions,
    setSelectedFeatureOptions
  };
};